// src/components/subscription/PaywallModal.jsx
import React, { useState } from 'react';
import { useSubscription } from '../../context/SubscriptionContext';
import { 
  XMarkIcon, 
  CheckIcon, 
  SparklesIcon, 
  LockClosedIcon,
  ShieldCheckIcon,
  ClockIcon
} from '@heroicons/react/24/solid';

export default function PaywallModal() {
  const { 
    isPaywallOpen, 
    closePaywall, 
    plans, 
    subscription, 
    openCheckout 
  } = useSubscription();

  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'yearly'

  if (!isPaywallOpen) return null;

  // Never display paywall overlay on login/signup screens
  if (typeof window !== 'undefined') {
    const path = window.location.pathname.toLowerCase();
    if (path.startsWith('/login') || path.startsWith('/signup')) {
      return null;
    }
  }

  const safePlans = Array.isArray(plans) && plans.length > 0 ? plans : [];
  const isExpired = subscription?.is_expired && !subscription?.is_super_admin;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md overflow-y-auto animate-fadeIn">
      <div className="relative w-full max-w-5xl bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden my-8">
        
        {/* Top Banner */}
        <div className="bg-gradient-to-r from-[#1B2A41] via-[#243B5A] to-[#1B2A41] text-white p-8 text-center relative overflow-hidden">
          {/* Subtle Background Glow */}
          <div className="absolute -top-24 -left-24 w-72 h-72 bg-[#E8612C]/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />

          {/* Close button only available if NOT expired or is super admin */}
          {!isExpired && (
            <button
              onClick={closePaywall}
              className="absolute top-6 right-6 text-gray-400 hover:text-white transition p-1 rounded-lg"
              title="Close"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          )}

          {isExpired ? (
            <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-4 animate-pulse">
              <LockClosedIcon className="w-4 h-4" />
              29-Day Free Trial Expired
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 bg-[#E8612C]/20 border border-[#E8612C]/40 text-[#E8612C] text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-4">
              <ClockIcon className="w-4 h-4" />
              {subscription?.days_remaining || 29} Days Remaining in Free Trial
            </div>
          )}

          <h2 className="text-2xl sm:text-3xl font-black text-white max-w-xl mx-auto leading-tight">
            {isExpired
              ? "Your Free Trial Has Ended — Choose a Plan to Continue"
              : "Upgrade Your BriqERP Experience"}
          </h2>
          <p className="text-sm text-gray-300 max-w-2xl mx-auto mt-3">
            {isExpired
              ? "To regain full access to your construction projects, budgets, payroll, and materials, select a plan below. All your company data is safe."
              : "Unlock higher user limits, automated payroll, vendor portals, and enterprise-grade reporting."}
          </p>

          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className={`text-xs font-semibold ${billingCycle === 'monthly' ? 'text-white' : 'text-gray-400'}`}>
              Monthly Billing
            </span>
            <button
              type="button"
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-[#E8612C] transition-colors focus:outline-none"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-xs font-semibold flex items-center gap-1.5 ${billingCycle === 'yearly' ? 'text-white' : 'text-gray-400'}`}>
              Annual Billing
              <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">
                Save 20%
              </span>
            </span>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="p-6 sm:p-8 bg-[#F7F4EE]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {safePlans.map((plan) => {
              const isPopular = plan.popular;
              const price = billingCycle === 'yearly'
                ? Math.round((plan.annual_price || plan.price * 10) / 12)
                : plan.price;

              return (
                <div
                  key={plan.id}
                  className={`relative bg-white rounded-2xl p-6 transition-all duration-200 flex flex-col justify-between ${
                    isPopular
                      ? 'border-2 border-[#E8612C] shadow-xl md:-translate-y-2'
                      : 'border border-gray-200 shadow-sm hover:shadow-md'
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#E8612C] text-white text-[11px] font-extrabold uppercase px-3 py-0.5 rounded-full shadow-md flex items-center gap-1">
                      <SparklesIcon className="w-3.5 h-3.5" />
                      Most Popular
                    </div>
                  )}

                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                    <p className="text-xs text-gray-500 mt-1 min-h-[36px]">
                      {plan.tagline}
                    </p>

                    {/* Price Display */}
                    <div className="mt-4 mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-black text-[#1B2A41]">
                          ₹{price.toLocaleString('en-IN')}
                        </span>
                        <span className="text-xs font-semibold text-gray-500">
                          / month
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        {billingCycle === 'yearly' ? 'Billed annually' : 'Billed monthly'}
                      </div>
                    </div>

                    {/* Feature List */}
                    <div className="space-y-2.5 border-t border-gray-100 pt-5">
                      <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                        What's Included:
                      </div>
                      {(Array.isArray(plan.features) ? plan.features : []).map((feat, idx) => (
                        <div key={idx} className="flex items-start gap-2.5 text-xs text-gray-600">
                          <CheckIcon className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="mt-8 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => openCheckout(plan, billingCycle)}
                      className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold uppercase tracking-wider transition shadow flex items-center justify-center gap-2 ${
                        isPopular
                          ? 'bg-[#E8612C] hover:bg-[#D55320] text-white shadow-[#E8612C]/20'
                          : 'bg-[#1B2A41] hover:bg-[#121E30] text-white'
                      }`}
                    >
                      <ShieldCheckIcon className="w-4 h-4" />
                      Select {plan.name}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center text-xs text-gray-500 flex flex-wrap items-center justify-center gap-6">
            <span>🛡️ Cancel or switch plans anytime</span>
            <span>💳 Seamless simulated gateway checkout</span>
            <span>⚡ Instant access activation upon payment</span>
          </div>
        </div>

      </div>
    </div>
  );
}
